/**
 * US ZIP code lookup, used to derive the billing city and state instead of
 * asking for them.
 *
 * `zipcodes-us` inlines the whole dataset in its entry module (~735 KB gzip)
 * and offers no remote loader, so it is imported on demand. Only the billing
 * address view needs it, and only while the card origin country is US.
 */

export interface ZipLocation {
  city: string;
  stateCode: string;
}

export type ZipLookup = (zipCode: string) => ZipLocation | null;

let pending: Promise<ZipLookup> | null = null;

/** Resolves once per session. The bundler caches the chunk after that. */
export const loadZipLookup = (): Promise<ZipLookup> => {
  pending ??= import("zipcodes-us")
    .then(
      ({ find }) =>
        (zipCode: string) => {
          const info = find(zipCode);
          return info.isValid
            ? { city: info.city, stateCode: info.stateCode }
            : null;
        },
    )
    .catch((error: unknown) => {
      // A failed chunk must not poison later attempts. `installChunkReload`
      // owns the reload when the preload itself is what broke.
      pending = null;
      throw error;
    });
  return pending;
};

/**
 * The dataset is keyed by the 5-digit prefix, so ZIP+4 resolves to the same
 * place. Anything else is still being typed.
 */
export const zipLookupKey = (zipCode: string): string | null => {
  const digits = zipCode.replace(/\D/g, "");
  return digits.length === 5 || digits.length === 9
    ? digits.slice(0, 5)
    : null;
};
